import type { Session } from "@fluentry/domain";
import {
  Button,
  Sheet,
  SheetContent,
  SheetClose,
  SheetDescription,
  SheetTitle,
  Sidebar,
  SidebarContent as SidebarFrameContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  StatusChip,
} from "@fluentry/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  BookOpenText,
  Command,
  FileText,
  History,
  LifeBuoy,
  LogOut,
  Plus,
  Settings,
  User,
  XIcon,
} from "lucide-react";
import React from "react";
import type { AuthSessionData } from "../context/app-context";
import { WorkspaceSwitcher } from "./workspace-switcher";

type SidebarRoute =
  | "/"
  | "/login"
  | "/help"
  | "/settings/profile"
  | "/settings/security"
  | "/settings/billing"
  | "/settings/organizations";

const SidebarNavLink = React.memo(function SidebarNavLink({
  to,
  activeHash,
  label,
  icon,
  exact = false,
  onNavigate,
  onSelect,
}: {
  to?: SidebarRoute;
  activeHash?: "#drafts" | "#history";
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
  onNavigate?: () => void;
  onSelect?: () => void;
}) {
  const location = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      hash: state.location.hash,
    }),
  });
  const activeForHash = activeHash
    ? location.pathname === "/" && location.hash === activeHash
    : false;
  const handleNavigate = React.useCallback(() => {
    // Closing the mobile drawer after the router click commits prevents the sheet unmount from swallowing the navigation.
    if (!onNavigate) {
      return;
    }

    window.setTimeout(() => {
      onNavigate();
    }, 0);
  }, [onNavigate]);

  const content = (
    <>
      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-sidebar-border bg-background text-sidebar-primary transition-colors group-data-active:bg-sidebar-primary group-data-active:text-sidebar-primary-foreground group-hover:bg-sidebar-primary/10 group-hover:text-sidebar-primary">
        {icon}
      </div>
      {/* Keep nav labels short so scanning the next action stays faster than parsing explanatory copy. */}
      <span className="text-sm font-semibold leading-none">{label}</span>
    </>
  );

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        size="lg"
        className="h-auto rounded-[1rem] px-3 py-2.5 data-active:shadow-sm"
      >
        {onSelect ? (
          // These shortcuts intentionally call back into the shell so cross-route jumps can coordinate with the workbench state.
          <button
            type="button"
            onClick={onSelect}
            data-active={activeForHash ? "true" : undefined}
            className="group flex min-h-11 w-full items-center gap-3 text-left"
          >
            {content}
          </button>
        ) : (
          <Link
            to={to!}
            onClick={handleNavigate}
            activeOptions={{ exact }}
            activeProps={{ "data-active": "true" }}
            className="group flex min-h-11 items-center gap-3"
          >
            {content}
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
});

const SidebarUserCard = React.memo(function SidebarUserCard({
  session,
  variant,
}: {
  session: Session;
  variant: "desktop" | "drawer";
}) {
  return (
    <div
      className={
        variant === "drawer"
          ? "mb-5 flex items-center gap-3 rounded-[1.25rem] border border-sidebar-border bg-card px-3 py-3 shadow-sm"
          : "mb-6 flex items-center gap-3 rounded-[1.25rem] border border-sidebar-border bg-card px-3 py-3 shadow-sm"
      }
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-sidebar-border bg-primary text-[13px] font-semibold text-primary-foreground">
        {session.user?.name?.charAt(0).toUpperCase() ?? "G"}
      </div>
      <div className="grid min-w-0 gap-0.5">
        <strong className="truncate text-sm font-semibold text-foreground">
          {session.user?.name ?? "Guest"}
        </strong>
        <span className="truncate text-xs text-muted-foreground">
          {session.user?.email ?? "Not signed in"}
        </span>
      </div>
      <div className="ml-auto shrink-0">
        <StatusChip status={session.authenticated ? "active" : "pending"}>
          {session.authenticated ? "Live" : "Guest"}
        </StatusChip>
      </div>
    </div>
  );
});

export function SidebarContent({
  session,
  authSession,
  onRefetchSession,
  onSignOut,
  onNavigateToSection,
  onCreateDraft,
  variant,
  onNavigate,
}: {
  session: Session;
  authSession: AuthSessionData | null;
  onRefetchSession: () => Promise<void>;
  onSignOut: () => Promise<void>;
  onNavigateToSection: (section: "drafts" | "history") => Promise<void>;
  onCreateDraft: () => Promise<void>;
  variant: "desktop" | "drawer";
  onNavigate?: () => void;
}) {
  const handleRouteLinkClick = React.useCallback(() => {
    // Delay drawer teardown until after the router click settles so utility routes remain reachable on mobile.
    if (!onNavigate) {
      return;
    }

    window.setTimeout(() => {
      onNavigate();
    }, 0);
  }, [onNavigate]);

  return (
    <>
      {variant === "desktop" ? (
        <SidebarHeader className="mb-4 px-0 pt-0">
          <div className="flex items-center gap-3 px-2 py-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-sidebar-border bg-background text-sidebar-primary shadow-sm">
              <Command className="h-4 w-4" />
            </div>
            <strong className="text-base font-semibold tracking-tight text-sidebar-foreground">
              Fluentry
            </strong>
          </div>
        </SidebarHeader>
      ) : null}

      <SidebarUserCard session={session} variant={variant} />
      <WorkspaceSwitcher
        authSession={authSession}
        onRefetchSession={onRefetchSession}
        onNavigate={onNavigate}
      />
      <Button
        aria-label="New draft"
        className="mb-6 h-11 justify-start rounded-[1rem] bg-primary text-primary-foreground hover:bg-primary/90"
        onClick={() => {
          void onCreateDraft().finally(() => onNavigate?.());
        }}
      >
        <Plus className="mr-2 h-4 w-4 text-primary-foreground/80" />
        New
      </Button>

      <SidebarFrameContent className="gap-0">
        <SidebarGroup className="p-0">
          <SidebarGroupLabel className="px-2 text-[10px] font-semibold tracking-[0.24em] uppercase">
            Workbench
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-2">
              <SidebarNavLink
                to="/"
                label="Workbench"
                icon={<BookOpenText className="h-4 w-4" />}
                exact
                onNavigate={onNavigate}
              />
              <SidebarNavLink
                activeHash="#drafts"
                label="Drafts"
                icon={<FileText className="h-4 w-4" />}
                onSelect={() => {
                  void onNavigateToSection("drafts").finally(() => onNavigate?.());
                }}
              />
              <SidebarNavLink
                activeHash="#history"
                label="History"
                icon={<History className="h-4 w-4" />}
                onSelect={() => {
                  void onNavigateToSection("history").finally(() => onNavigate?.());
                }}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFrameContent>

      <SidebarFooter
        className={
          variant === "drawer" ? "mt-auto gap-3 pt-6" : "mt-6 gap-3 px-0 pt-2 sm:mt-auto sm:pt-8"
        }
      >
        <Button
          asChild
          variant="outline"
          className="h-11 justify-start rounded-[1rem] bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
        >
          <Link to="/settings/profile" onClick={handleRouteLinkClick}>
            <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
            Settings
          </Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="h-11 justify-start rounded-[1rem] bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
        >
          <Link to="/help" onClick={handleRouteLinkClick}>
            <LifeBuoy className="mr-2 h-4 w-4 text-muted-foreground" />
            Help
          </Link>
        </Button>
        {session.authenticated ? (
          <Button
            variant="outline"
            className="h-11 justify-start rounded-[1rem] bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
            onClick={() => void onSignOut()}
          >
            <LogOut className="mr-2 h-4 w-4 text-muted-foreground" />
            Sign out
          </Button>
        ) : (
          <Button
            asChild
            className="h-11 justify-start rounded-[1rem] bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link to="/login">
              <User className="mr-2 h-4 w-4 text-primary-foreground/80" />
              Sign in
            </Link>
          </Button>
        )}
      </SidebarFooter>
    </>
  );
}

export function MobileNavigationDrawer({
  open,
  session,
  authSession,
  onRefetchSession,
  onSignOut,
  onNavigateToSection,
  onCreateDraft,
  onClose,
}: {
  open: boolean;
  session: Session;
  authSession: AuthSessionData | null;
  onRefetchSession: () => Promise<void>;
  onSignOut: () => Promise<void>;
  onNavigateToSection: (section: "drafts" | "history") => Promise<void>;
  onCreateDraft: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
    >
      <SheetContent
        side="left"
        showCloseButton={false}
        className="w-[min(86vw,320px)] overflow-y-auto border-r border-border bg-background p-0 text-foreground sm:hidden"
        data-testid="mobile-nav-drawer"
      >
        <SheetTitle className="sr-only">Navigation menu</SheetTitle>
        <SheetDescription className="sr-only">
          Navigate between workbench, saved drafts, history, help, settings, and account actions.
        </SheetDescription>
        <SidebarProvider defaultOpen>
          <div className="flex min-h-full flex-col px-4 py-5" data-testid="mobile-nav-panel">
            <div
              className="mb-3 flex items-center justify-between px-2"
              data-testid="mobile-nav-header"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-border bg-card text-primary shadow-sm">
                  <Command className="h-4 w-4" />
                </div>
                <strong className="text-base font-semibold tracking-tight text-foreground">
                  Menu
                </strong>
              </div>
              {/* The drawer close button is labeled with product context so the mobile shell keeps the same affordance as the desktop sidebar. */}
              <SheetClose asChild>
                <Button variant="ghost" size="icon-sm" className="rounded-full">
                  <XIcon className="h-4 w-4" />
                  <span className="sr-only">Close navigation menu</span>
                </Button>
              </SheetClose>
            </div>
            <SidebarContent
              session={session}
              authSession={authSession}
              onRefetchSession={onRefetchSession}
              onSignOut={onSignOut}
              onNavigateToSection={onNavigateToSection}
              onCreateDraft={onCreateDraft}
              variant="drawer"
              onNavigate={onClose}
            />
          </div>
        </SidebarProvider>
      </SheetContent>
    </Sheet>
  );
}

const BottomNavTab = React.memo(function BottomNavTab({
  label,
  icon,
  active,
  to,
  exact = false,
  onSelect,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  to?: SidebarRoute;
  exact?: boolean;
  onSelect?: () => void;
}) {
  // Touch targets stay at least 56px tall so the bar reads as a native tab bar, not a dense web toolbar.
  const className =
    "group flex min-h-[3.5rem] flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[10px] font-semibold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  const tone = active ? "text-primary" : "text-muted-foreground hover:text-foreground";
  const iconWrap = `flex h-7 w-12 items-center justify-center rounded-full transition-colors ${
    active ? "bg-primary/12 text-primary" : "text-muted-foreground group-hover:text-foreground"
  }`;
  const body = (
    <>
      <span className={iconWrap}>{icon}</span>
      <span>{label}</span>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        activeOptions={{ exact }}
        className={`${className} ${tone}`}
        aria-current={active ? "page" : undefined}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`${className} ${tone}`}
      aria-current={active ? "page" : undefined}
    >
      {body}
    </button>
  );
});

export function MobileBottomNav({
  session,
  onNavigateToSection,
  onCreateDraft,
}: {
  session: Session;
  onNavigateToSection: (section: "drafts" | "history") => Promise<void>;
  onCreateDraft: () => Promise<void>;
}) {
  const location = useRouterState({
    select: (state) => ({ pathname: state.location.pathname, hash: state.location.hash }),
  });
  const onWorkbenchRoute = location.pathname === "/";
  const draftsActive = onWorkbenchRoute && location.hash === "#drafts";
  const historyActive = onWorkbenchRoute && location.hash === "#history";
  const workbenchActive = onWorkbenchRoute && !draftsActive && !historyActive;
  const accountActive = location.pathname.startsWith("/settings");

  return (
    <nav
      aria-label="Primary"
      data-testid="mobile-bottom-nav"
      // Fixed so the workbench keeps a persistent app-style tab bar; safe-area padding clears the home indicator.
      // Slide the bar down by the soft-keyboard height so it never overlaps a focused input; offset is 0px when hidden.
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border/80 bg-background/95 backdrop-blur-xl transition-transform duration-150 sm:hidden"
      style={{
        paddingBottom: "var(--safe-area-bottom)",
        transform: "translateY(var(--mobile-keyboard-offset, 0px))",
      }}
    >
      {/* Body height is tab min-h (3.5rem) + pt-2 = 64px, plus safe-area padding on the nav, for a native-sized tab bar. */}
      <div className="mx-auto grid max-w-md grid-cols-5 items-end px-2 pt-2">
        <BottomNavTab
          label="Workbench"
          icon={<BookOpenText className="h-[1.3rem] w-[1.3rem]" />}
          active={workbenchActive}
          to="/"
          exact
        />
        <BottomNavTab
          label="Drafts"
          icon={<FileText className="h-[1.3rem] w-[1.3rem]" />}
          active={draftsActive}
          onSelect={() => void onNavigateToSection("drafts")}
        />
        <div className="flex min-h-[3.5rem] flex-col items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => void onCreateDraft()}
            aria-label="New draft"
            className="-mt-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Plus className="h-6 w-6" />
          </button>
          {/* Decorative twin of the button label; the button itself carries the accessible name, so hide this from AT. */}
          <span
            aria-hidden="true"
            className="text-[10px] font-semibold tracking-tight text-muted-foreground"
          >
            New
          </span>
        </div>
        <BottomNavTab
          label="History"
          icon={<History className="h-[1.3rem] w-[1.3rem]" />}
          active={historyActive}
          onSelect={() => void onNavigateToSection("history")}
        />
        <BottomNavTab
          label="Account"
          icon={
            session.authenticated ? (
              <User className="h-[1.3rem] w-[1.3rem]" />
            ) : (
              <LogOut className="h-[1.3rem] w-[1.3rem]" />
            )
          }
          active={accountActive}
          to={session.authenticated ? "/settings/profile" : "/login"}
        />
      </div>
    </nav>
  );
}

export function DesktopNavigationSidebar({
  session,
  authSession,
  onRefetchSession,
  onSignOut,
  onNavigateToSection,
  onCreateDraft,
}: {
  session: Session;
  authSession: AuthSessionData | null;
  onRefetchSession: () => Promise<void>;
  onSignOut: () => Promise<void>;
  onNavigateToSection: (section: "drafts" | "history") => Promise<void>;
  onCreateDraft: () => Promise<void>;
}) {
  return (
    <SidebarProvider defaultOpen>
      {/* Use the shared sidebar primitive on desktop so navigation semantics stay simpler than a custom shell layout. */}
      <Sidebar collapsible="none" className="h-full w-full">
        <SidebarContent
          session={session}
          authSession={authSession}
          onRefetchSession={onRefetchSession}
          onSignOut={onSignOut}
          onNavigateToSection={onNavigateToSection}
          onCreateDraft={onCreateDraft}
          variant="desktop"
        />
      </Sidebar>
    </SidebarProvider>
  );
}
